import { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, RefreshCw, LogOut, Lock, Eye, Download } from "lucide-react";
import { SEO } from "@/components/SEO";

type Bucket = { name: string; value: number };
type Daily = { date: string; count: number };

type Stats = {
  days: number;
  totals: {
    pageviews: number;
    whitepaperLeads: number;
    businessCaseLeads: number;
    totalLeads: number;
    conversionRate: number;
  };
  pageviews: {
    byCountry: Bucket[];
    bySource: Bucket[];
    byMedium: Bucket[];
    byCampaign: Bucket[];
    byPath: Bucket[];
    byReferrer: Bucket[];
    daily: Daily[];
  };
  utmCampaigns: UtmCampaignRow[];
  leads: {
    byCountry: Bucket[];
    bySource: Bucket[];
    byCampaign: Bucket[];
    daily: Daily[];
    whitepaperByCountry: Bucket[];
    businessCaseByCountry: Bucket[];
    whitepaperRecent: WhitepaperLead[];
    businessCaseRecent: BusinessCaseLead[];
  };
};

export type UtmCampaignRow = {
  source: string;
  medium: string;
  campaign: string;
  sessions: number;
  pageviews: number;
  conversions: number;
};

export type WhitepaperLead = {
  name: string;
  email: string;
  company: string | null;
  position: string | null;
  country: string | null;
  referrer_host: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  created_at: string;
};

export type BusinessCaseLead = {
  name: string;
  position: string;
  company: string;
  current_situation: string;
  perspective: string;
  country: string | null;
  referrer_host: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  created_at: string;
};

const COUNTRY_COLORS: Record<string, string> = {
  DE: "hsl(217 71% 25%)",
  AT: "hsl(0 70% 50%)",
  CH: "hsl(45 90% 50%)",
  FR: "hsl(220 70% 55%)",
  OTHER: "hsl(220 9% 60%)",
  UNKNOWN: "hsl(220 9% 75%)",
};

const PIE_COLORS = [
  "hsl(217 71% 25%)",
  "hsl(45 90% 50%)",
  "hsl(0 70% 50%)",
  "hsl(220 70% 55%)",
  "hsl(160 60% 40%)",
  "hsl(280 60% 50%)",
  "hsl(30 80% 55%)",
  "hsl(220 9% 60%)",
];

const SUPABASE_PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID as string;
const FN_URL = `https://${SUPABASE_PROJECT_ID}.functions.supabase.co/dashboard-stats`;
const HASH_STORAGE_KEY = "dashboard_token_hash";

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

const Dashboard = () => {
  const [tokenHash, setTokenHash] = useState<string | null>(() =>
    typeof window !== "undefined" ? localStorage.getItem(HASH_STORAGE_KEY) : null,
  );
  const [tokenInput, setTokenInput] = useState("");
  const [days, setDays] = useState<number>(30);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Falls jemand einen alten Link mit ?token=… aufruft → sofort aus URL entfernen.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("token")) {
      params.delete("token");
      const clean = window.location.pathname + (params.toString() ? `?${params}` : "");
      window.history.replaceState({}, "", clean);
    }
  }, []);

  const fetchStats = async (hash: string, d: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${FN_URL}?days=${d}`, {
        headers: { "x-dashboard-token-hash": hash },
      });
      if (res.status === 401) {
        localStorage.removeItem(HASH_STORAGE_KEY);
        setTokenHash(null);
        throw new Error("Token ungültig — bitte neu eingeben.");
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setStats(await res.json());
    } catch (e) {
      setError((e as Error).message);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tokenHash) fetchStats(tokenHash, days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenHash]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tokenInput) return;
    const hash = await sha256Hex(tokenInput);
    localStorage.setItem(HASH_STORAGE_KEY, hash);
    setTokenInput("");
    setTokenHash(hash);
  };

  const handleLogout = () => {
    localStorage.removeItem(HASH_STORAGE_KEY);
    setTokenHash(null);
    setStats(null);
  };

  const conversionPct = useMemo(
    () => (stats ? (stats.totals.conversionRate * 100).toFixed(2) : "0.00"),
    [stats],
  );

  // Login-Maske
  if (!tokenHash) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <SEO title="Dashboard | Intraprenör" description="Internes Analytics-Dashboard." path="/dashboard" noindex />
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Dashboard-Zugang
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground block mb-1">
                  Zugangs-Token
                </label>
                <Input
                  type="password"
                  value={tokenInput}
                  onChange={(e) => setTokenInput(e.target.value)}
                  placeholder="Token eingeben"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Wird lokal als Hash gespeichert und nie als URL-Parameter übertragen.
                </p>
              </div>
              <Button type="submit" className="w-full" disabled={!tokenInput}>
                Anmelden
              </Button>
              {error && <p className="text-sm text-destructive">{error}</p>}
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-bold text-foreground">
              UTM-Analytics-Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Anonyme Auswertung von Seitenaufrufen und Leads — kein Consent erforderlich.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Zeitraum (Tage)</label>
              <Input
                type="number"
                min={1}
                max={365}
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value || "30", 10))}
                className="w-24"
              />
            </div>
            <Button onClick={() => fetchStats(tokenHash, days)} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-2">Aktualisieren</span>
            </Button>
            <Button variant="outline" onClick={handleLogout} title="Abmelden">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        {error && (
          <Card className="border-destructive/50">
            <CardContent className="pt-6 text-destructive">Fehler: {error}</CardContent>
          </Card>
        )}

        {stats && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <KPI label="Seitenaufrufe" value={stats.totals.pageviews} />
              <KPI label="Whitepaper-Leads" value={stats.totals.whitepaperLeads} />
              <KPI label="Business-Case-Anfragen" value={stats.totals.businessCaseLeads} />
              <KPI label="Leads gesamt" value={stats.totals.totalLeads} />
              <KPI label="Conversion-Rate" value={`${conversionPct} %`} />
            </div>

            {/* Time series */}
            <Card>
              <CardHeader>
                <CardTitle>Verlauf (letzte {stats.days} Tage)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={mergeDaily(stats.pageviews.daily, stats.leads.daily)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" />
                      <YAxis stroke="hsl(var(--muted-foreground))" />
                      <Tooltip
                        contentStyle={{
                          background: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                        }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="pageviews" stroke="hsl(217 71% 25%)" strokeWidth={2} />
                      <Line type="monotone" dataKey="leads" stroke="hsl(45 90% 50%)" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Tabs defaultValue="country" className="w-full">
              <TabsList>
                <TabsTrigger value="country">Land</TabsTrigger>
                <TabsTrigger value="utm">UTM-Quellen</TabsTrigger>
                <TabsTrigger value="content">Inhalte & Referrer</TabsTrigger>
                <TabsTrigger value="leads">Leads</TabsTrigger>
              </TabsList>

              <TabsContent value="country" className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <ChartCard title="Seitenaufrufe nach Land">
                    <CountryPie data={stats.pageviews.byCountry} />
                  </ChartCard>
                  <ChartCard title="Leads nach Land">
                    <CountryPie data={stats.leads.byCountry} />
                  </ChartCard>
                  <ChartCard title="Whitepaper-Leads nach Land">
                    <CountryBars data={stats.leads.whitepaperByCountry} />
                  </ChartCard>
                  <ChartCard title="Business-Case-Anfragen nach Land">
                    <CountryBars data={stats.leads.businessCaseByCountry} />
                  </ChartCard>
                </div>
              </TabsContent>

              <TabsContent value="utm" className="space-y-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between gap-4">
                    <CardTitle className="text-base">UTM-Kampagnen</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadCsv("utm-campaigns.csv", stats.utmCampaigns ?? [])}
                    >
                      <Download className="h-4 w-4 mr-2" /> CSV
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <UtmCampaignsTable rows={stats.utmCampaigns ?? []} />
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <ChartCard title="Seitenaufrufe nach UTM-Source">
                    <Bars data={stats.pageviews.bySource} />
                  </ChartCard>
                  <ChartCard title="Seitenaufrufe nach UTM-Medium">
                    <Bars data={stats.pageviews.byMedium} />
                  </ChartCard>
                  <ChartCard title="Seitenaufrufe nach Kampagne">
                    <Bars data={stats.pageviews.byCampaign} />
                  </ChartCard>
                  <ChartCard title="Leads nach UTM-Source">
                    <Bars data={stats.leads.bySource} />
                  </ChartCard>
                  <ChartCard title="Leads nach Kampagne">
                    <Bars data={stats.leads.byCampaign} />
                  </ChartCard>
                </div>
              </TabsContent>

              <TabsContent value="content" className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <ChartCard title="Top-Seiten (Pfad)">
                    <Bars data={stats.pageviews.byPath} horizontal />
                  </ChartCard>
                  <ChartCard title="Top-Referrer">
                    <Bars data={stats.pageviews.byReferrer} horizontal />
                  </ChartCard>
                </div>
              </TabsContent>

              <TabsContent value="leads" className="space-y-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between gap-4">
                    <CardTitle className="text-base">
                      Whitepaper-Downloads ({stats.leads.whitepaperRecent.length})
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadCsv("whitepaper-leads.csv", stats.leads.whitepaperRecent)}
                    >
                      <Download className="h-4 w-4 mr-2" /> CSV
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <WhitepaperTable rows={stats.leads.whitepaperRecent} />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between gap-4">
                    <CardTitle className="text-base">
                      Business-Case-Anfragen ({stats.leads.businessCaseRecent.length})
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadCsv("business-case-requests.csv", stats.leads.businessCaseRecent)}
                    >
                      <Download className="h-4 w-4 mr-2" /> CSV
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <BusinessCaseTable rows={stats.leads.businessCaseRecent} />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
};

const KPI = ({ label, value }: { label: string; value: number | string }) => (
  <Card>
    <CardContent className="pt-6">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl md:text-3xl font-bold text-foreground mt-1">
        {typeof value === "number" ? value.toLocaleString("de-DE") : value}
      </div>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-base">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="h-64">{children}</div>
    </CardContent>
  </Card>
);

const CountryPie = ({ data }: { data: Bucket[] }) => (
  <ResponsiveContainer width="100%" height="100%">
    <PieChart>
      <Pie data={data} dataKey="value" nameKey="name" outerRadius={80} label>
        {data.map((entry, i) => (
          <Cell key={i} fill={COUNTRY_COLORS[entry.name] ?? PIE_COLORS[i % PIE_COLORS.length]} />
        ))}
      </Pie>
      <Tooltip />
      <Legend />
    </PieChart>
  </ResponsiveContainer>
);

const CountryBars = ({ data }: { data: Bucket[] }) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data}>
      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
      <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
      <YAxis stroke="hsl(var(--muted-foreground))" />
      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
      <Bar dataKey="value">
        {data.map((entry, i) => (
          <Cell key={i} fill={COUNTRY_COLORS[entry.name] ?? PIE_COLORS[i % PIE_COLORS.length]} />
        ))}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const Bars = ({ data, horizontal = false }: { data: Bucket[]; horizontal?: boolean }) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data} layout={horizontal ? "vertical" : "horizontal"}>
      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
      {horizontal ? (
        <>
          <XAxis type="number" stroke="hsl(var(--muted-foreground))" />
          <YAxis dataKey="name" type="category" width={140} stroke="hsl(var(--muted-foreground))" />
        </>
      ) : (
        <>
          <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
          <YAxis stroke="hsl(var(--muted-foreground))" />
        </>
      )}
      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
      <Bar dataKey="value" fill="hsl(217 71% 25%)" />
    </BarChart>
  </ResponsiveContainer>
);

function mergeDaily(pv: Daily[], leads: Daily[]) {
  const map = new Map<string, { date: string; pageviews: number; leads: number }>();
  for (const p of pv) map.set(p.date, { date: p.date, pageviews: p.count, leads: 0 });
  for (const l of leads) {
    const existing = map.get(l.date);
    if (existing) existing.leads = l.count;
    else map.set(l.date, { date: l.date, pageviews: 0, leads: l.count });
  }
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("de-DE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function downloadCsv(filename: string, rows: Record<string, unknown>[]) {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const escape = (v: unknown) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n;]/.test(s) ? `"${s}"` : s;
  };
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const CountryBadge = ({ code }: { code: string | null }) => {
  const c = code ?? "—";
  const color = COUNTRY_COLORS[c] ?? "hsl(var(--muted-foreground))";
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
      style={{ background: color }}
    >
      {c}
    </span>
  );
};

const UtmCell = ({ source, medium, campaign }: { source: string | null; medium: string | null; campaign: string | null }) => {
  if (!source && !medium && !campaign) return <span className="text-muted-foreground text-xs">direct</span>;
  return (
    <div className="text-xs space-y-0.5">
      {source && <div><span className="text-muted-foreground">src:</span> {source}</div>}
      {medium && <div><span className="text-muted-foreground">med:</span> {medium}</div>}
      {campaign && <div><span className="text-muted-foreground">cmp:</span> {campaign}</div>}
    </div>
  );
};

const UtmCampaignsTable = ({ rows }: { rows: UtmCampaignRow[] }) => {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground py-8 text-center">Keine UTM-Daten im Zeitraum.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Source</TableHead>
            <TableHead>Medium</TableHead>
            <TableHead>Campaign</TableHead>
            <TableHead className="text-right">Sessions</TableHead>
            <TableHead className="text-right">Pageviews</TableHead>
            <TableHead className="text-right">Conversions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell className="text-sm">{r.source}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{r.medium}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{r.campaign}</TableCell>
              <TableCell className="text-right tabular-nums">{r.sessions}</TableCell>
              <TableCell className="text-right tabular-nums">{r.pageviews}</TableCell>
              <TableCell className={`text-right tabular-nums font-medium ${r.conversions > 0 ? "text-foreground" : "text-muted-foreground"}`}>
                {r.conversions}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const WhitepaperTable = ({ rows }: { rows: WhitepaperLead[] }) => {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground py-8 text-center">Keine Whitepaper-Downloads im Zeitraum.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Datum</TableHead>
            <TableHead>Land</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>E-Mail</TableHead>
            <TableHead>Firma</TableHead>
            <TableHead>Position</TableHead>
            <TableHead>UTM</TableHead>
            <TableHead>Referrer</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell className="text-xs whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
              <TableCell><CountryBadge code={r.country} /></TableCell>
              <TableCell className="text-sm">{r.name}</TableCell>
              <TableCell className="text-sm">
                <a href={`mailto:${r.email}`} className="text-primary hover:underline">{r.email}</a>
              </TableCell>
              <TableCell className="text-sm">{r.company || "—"}</TableCell>
              <TableCell className="text-sm">{r.position || "—"}</TableCell>
              <TableCell><UtmCell source={r.utm_source} medium={r.utm_medium} campaign={r.utm_campaign} /></TableCell>
              <TableCell className="text-xs text-muted-foreground">{r.referrer_host || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const BusinessCaseTable = ({ rows }: { rows: BusinessCaseLead[] }) => {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground py-8 text-center">Keine Business-Case-Anfragen im Zeitraum.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Datum</TableHead>
            <TableHead>Land</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Position</TableHead>
            <TableHead>Firma</TableHead>
            <TableHead>UTM</TableHead>
            <TableHead className="text-right">Details</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell className="text-xs whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
              <TableCell><CountryBadge code={r.country} /></TableCell>
              <TableCell className="text-sm">{r.name}</TableCell>
              <TableCell className="text-sm">{r.position}</TableCell>
              <TableCell className="text-sm">{r.company}</TableCell>
              <TableCell><UtmCell source={r.utm_source} medium={r.utm_medium} campaign={r.utm_campaign} /></TableCell>
              <TableCell className="text-right">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm"><Eye className="h-4 w-4" /></Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{r.name} — {r.company}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 text-sm">
                      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span>{fmtDate(r.created_at)}</span>
                        <span>•</span>
                        <CountryBadge code={r.country} />
                        <span>•</span>
                        <span>{r.position}</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">Aktuelle Situation</h4>
                        <p className="whitespace-pre-wrap text-muted-foreground">{r.current_situation}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-1">Perspektive</h4>
                        <p className="whitespace-pre-wrap text-muted-foreground">{r.perspective}</p>
                      </div>
                      {(r.utm_source || r.utm_medium || r.utm_campaign || r.utm_term || r.utm_content || r.referrer_host) && (
                        <div>
                          <h4 className="font-semibold text-foreground mb-1">Tracking</h4>
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                            {r.utm_source && (<><dt className="text-muted-foreground">UTM Source</dt><dd>{r.utm_source}</dd></>)}
                            {r.utm_medium && (<><dt className="text-muted-foreground">UTM Medium</dt><dd>{r.utm_medium}</dd></>)}
                            {r.utm_campaign && (<><dt className="text-muted-foreground">UTM Campaign</dt><dd>{r.utm_campaign}</dd></>)}
                            {r.utm_term && (<><dt className="text-muted-foreground">UTM Term</dt><dd>{r.utm_term}</dd></>)}
                            {r.utm_content && (<><dt className="text-muted-foreground">UTM Content</dt><dd>{r.utm_content}</dd></>)}
                            {r.referrer_host && (<><dt className="text-muted-foreground">Referrer</dt><dd>{r.referrer_host}</dd></>)}
                          </dl>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default Dashboard;
